import { useState, useContext, useRef, useEffect } from "react";
import { useDrag } from "react-dnd";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { AppContext } from "../../Contexts/Contexts";
import BrowseConstants from "../../store/browse.constants";

const PageNode = ({
  context,
  page,
  selectedPageContexts,
  onDoubleClick,
  onClick,
}) => {
  const { blocker } = useContext(AppContext);
  const { documentIndex, pageIndex } = context;
  const alt = `document-${documentIndex + 1}-page-${pageIndex + 1}`;
  const magnifyRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [rect, setRect] = useState(null);

  const borderWidth = 2;
  const selected = selectedPageContexts.has(JSON.stringify(context));
  const borderColor = selected ? "#086375" : "#f0f0f0";
  const scale = hover ? "scale(1.05)" : "scale(1)";

  const [{ isDragging }, drag] = useDrag(() => {
    return {
      type: "page",
      item: { type: "page", draggedContext: context, selectedPageContexts },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    };
  }, [selectedPageContexts, context]);

  useEffect(() => {
    if (magnifyRef.current) {
      setRect(magnifyRef.current.getBoundingClientRect());
    }
  }, [magnifyRef, hover]);

  return (
    <div
      ref={drag}
      className={`relative bg-white grab shadow`}
      title={`Page Confidence: ${page.docConf}, Threshold: ${page.confThresh}`}
      style={{
        opacity: isDragging ? 0 : 1,
        border: `${borderWidth}px solid ${borderColor}`,
        width: `${BrowseConstants.THUMBNAIL_WIDTH}px`,
        height: `${BrowseConstants.THUMBNAIL_HEIGHT}px`,
        transition: "transform 0.3s ease",
        transform: `${scale}`,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <img
        src={page.thumbnail}
        alt={alt}
        style={{
          maxWidth: `${BrowseConstants.THUMBNAIL_WIDTH - borderWidth * 2}px`,
          maxHeight: `${BrowseConstants.THUMBNAIL_HEIGHT - borderWidth * 2}px`,
        }}
      />
      {hover && (
        <div
          ref={magnifyRef}
          className="fade-in absolute flex items-center p-1"
          onMouseEnter={() => {
            timeoutIdRef.current = setTimeout(() => {
              blocker.open(
                <img
                  className="fade-in"
                  src={page.src}
                  alt={alt}
                  style={{ height: "100vh" }}
                  onClick={() => blocker.close()}
                />
              );
              const onMouseMove = (e) => {
                if (!rect) return;
                const { top, left, width, height } = rect;
                const { clientX, clientY } = e;
                const isInside =
                  clientX >= left &&
                  clientX <= left + width &&
                  clientY >= top &&
                  clientY <= top + height;
                if (!isInside) {
                  blocker.close();
                  window.removeEventListener("mousemove", onMouseMove);
                }
              };
              window.addEventListener("mousemove", onMouseMove);
            }, 500);
          }}
          onMouseLeave={() => {
            if (timeoutIdRef.current) {
              clearTimeout(timeoutIdRef.current);
              timeoutIdRef.current = null;
            }
          }}
          style={{ bottom: "0px", right: "0px" }}
        >
          <FaMagnifyingGlass size={20} color="gray" />
        </div>
      )}
    </div>
  );
};

export default PageNode;
